/*
 * Copyright (c) 2024 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, August 2nd 2024, 10:23:19 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import AddBoxIcon from "@mui/icons-material/AddBox";
import CodeIcon from "@mui/icons-material/Code";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TableChartIcon from "@mui/icons-material/TableChart";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useTranslatedText } from "core";
import { withAccessCheck } from "authorization/hoc";
import { ProcessInstanceDataTableResult } from "lib/schemas";
import { DesignLayoutEditor } from "service-builder/components/layouts/DesignLayoutEditor";
import { useServiceBuilderContext } from "service-builder/providers";
import { useProcessDefinitionsAPI } from "service-builder/services";
import {
  OSActionButton,
  ListButtonAutoComplete,
  SQLEditor,
  StyledOSDialog,
  JSONEditor,
  OSDropDown,
  OSIconButton,
  ActionDialog,
  AlertSnackBar,
  OSAlert,
  ProgressWrapper,
  SimpleTable,
} from "ui-widgets";

const SQLEditorForEntepriseCustomers = withAccessCheck(
  {
    resourceId: "runSQLQueries",
    invalidMessage: "You need an enterprise plan to run complex but safe SQL queries for custom list views.",
  },
  SQLEditor
);

const DATA_QUERY_TYPE_OPTIONS = {
  options: { json: "Basic Configuration", query: "SQL Query" },
  icons: { json: <CodeIcon color="secondary" />, query: <CodeIcon color="secondary" /> },
  description: {
    json: "Simply select fields and add them to the column",
    query: "For complex and custom data querying, provide SQL queries",
  },
};

type TDataTableColumnDefinition = {
  col: string;
  label: string;
  link?: boolean;
  order?: "asc" | "desc";
  staff_view?: boolean;
  progress_widget?: "" | "linear";
};

type TDataTableConfig = {
  query?: string;
  columns: TDataTableColumnDefinition[];
};

export const ListLayoutEditor = ({
  designLayout,
  onChangeValue,
}: React.ComponentProps<React.ComponentProps<typeof DesignLayoutEditor>["CustomEditorComponent"]>) => {
  const LANG = useTranslatedText();
  const { useAPIProcessInstanceDTQuery } = useProcessDefinitionsAPI();
  const {
    fetchDTData,
    isMakingAPIRequest: isLoadingDataTableData,
    isAPIError: isDTFetchError,
    apiErrors: dtFetchErrors,
  } = useAPIProcessInstanceDTQuery();
  const {
    processDefinitionContext: { currentProcessDefinition },
  } = useServiceBuilderContext();

  const [dataTableQueryResults, setSDataTableQueryResults] = React.useState<ProcessInstanceDataTableResult>();
  const [showJSONEditor, setShowJSONEditor] = React.useState(false);
  const [changedQueryTypeBeforeConfirmation, setChangedQueryTypeBeforeConfirmation] =
    React.useState<keyof typeof DATA_QUERY_TYPE_OPTIONS["options"]>();

  const toggleLayoutActive = (value: boolean) => {
    onChangeValue("active", value, { shouldDirty: true });
  };
  const layoutIsActive = designLayout.active;

  let config = null;
  try {
    config = JSON.parse(designLayout.html_design);
  } catch (error) {
    console.error("Error parsing JSON", error);
    config = {};
  }

  const query = config?.query;
  const columns = config?.columns || [];

  const onChangeConfiguration = (value: Partial<TDataTableConfig>) => {
    const newConfig = { columns: [], ...config, ...value };
    onChangeValue("html_design" as any, JSON.stringify(newConfig, null, 2), {
      shouldDirty: true,
    });
  };

  const getCurrentDataQueryType = () => {
    if (query === null || query === undefined) return "json";
    return "query";
  };

  const onConfirmChangeDataQueryType = () => {
    switch (changedQueryTypeBeforeConfirmation) {
      case "query":
        onChangeConfiguration({ query: "" });
        break;
      default:
        const newConfig = JSON.stringify({ columns: [] }, null, 2);
        onChangeValue("html_design", newConfig);
    }
  };

  const getQueryColumns = (query: string) => {
    const selectFromPattern = /select\s+(.*?)\s+from/i;
    const EMPTY = " ";
    const match = query.replace("\n", EMPTY).replace("\t", EMPTY).match(selectFromPattern);
    if (match && match[1]) {
      return match[1].split(",").map((field) => field.trim());
    }
    return [];
  };

  const onChangeSQLQuery = (query: string) => {
    let newColumnsWithDefaults = [...columns];
    getQueryColumns(query).forEach((col, index) => {
      if (columns[index] !== undefined) return;

      const defaultNewColumn: TDataTableColumnDefinition = {
        col: col.trim(),
        label: col.trim(),
        link: false,
        order: "asc",
        staff_view: false,
      };
      newColumnsWithDefaults[index] = defaultNewColumn;
    });

    onChangeConfiguration({ query, columns: newColumnsWithDefaults });
  };

  const handleFetchDTData = () => {
    fetchDTData(currentProcessDefinition.id, setSDataTableQueryResults);
  };

  let editor = null;
  let editorSettings = null;
  let columnsErrorMessage = null;

  switch (getCurrentDataQueryType()) {
    case "json":
      editor = (
        <JSONConfigurationEditor
          columns={columns}
          onChangeColumns={(columns) => onChangeConfiguration({ columns })}
          showJSONEditor={showJSONEditor}
        />
      );
      const icon = showJSONEditor ? <TableChartIcon /> : <CodeIcon />;
      const toolTipText = <Typography>{`${showJSONEditor ? "Edit in Table" : "Edit JSON"}`}</Typography>;
      editorSettings = (
        <div>
          <OSIconButton
            color="primary"
            icon={icon}
            toolTipText={toolTipText}
            onClick={() => setShowJSONEditor(!showJSONEditor)}
          />{" "}
        </div>
      );
      break;
    case "query":
      editor = (
        <>
          <SQLEditorForEntepriseCustomers
            editorId="list-layout-sql-editor"
            query={query}
            onChangeQuery={onChangeSQLQuery}
            onRunQuery={handleFetchDTData}
          />
          <ProcessInstanceDataTableResultsPreview
            dataTableQueryResults={dataTableQueryResults}
            columns={columns}
            isLoading={isLoadingDataTableData}
            hasError={isDTFetchError}
            errors={dtFetchErrors}
          />
        </>
      );
      editorSettings = (
        <ManageDataTableColumnsPopUpButton
          columns={columns}
          onChangeColumns={(columns) => onChangeConfiguration({ columns })}
        />
      );
      const queryColumns = getQueryColumns(query);
      columnsErrorMessage = (
        <AlertSnackBar
          handleClose={() => {}}
          open={columns.length !== queryColumns.length}
          severity="warning"
          message={`Query columns count mismatch. Your SQL query is returning ${queryColumns.length} columns but you have defined ${columns.length} datatable columns`}
          canBeClosedByUser={false}
        />
      );
      break;
  }

  return (
    <>
      {columnsErrorMessage}
      <Stack direction="row" spacing={2}>
        <Stack sx={{ minWidth: "80%" }}>{editor}</Stack>

        <Stack spacing={2} sx={{ border: "1px solid var(--grey-01)", padding: "1rem", minWidth: "20%" }}>
          <ActionDialog
            title={LANG.changeDataQueryType}
            severity="warning"
            confirmationAction={onConfirmChangeDataQueryType}
            dialogMessage={<>{LANG.changeDataQueryTypeConfirmation}</>}
          >
            {({ handleOpen }) => {
              return (
                <ListButtonAutoComplete
                  textFieldProps={{ label: "Data Query Type" }}
                  value={getCurrentDataQueryType()}
                  getOptionLabel={(option) => DATA_QUERY_TYPE_OPTIONS.options[option]}
                  getPrimaryOptionLabel={(option) => (
                    <Stack direction="row" spacing={2}>
                      <div>{DATA_QUERY_TYPE_OPTIONS.icons[option]}</div>
                      <Typography variant="body1">{DATA_QUERY_TYPE_OPTIONS.options[option]}</Typography>
                    </Stack>
                  )}
                  getSecondaryOptionLabel={(option) => DATA_QUERY_TYPE_OPTIONS.description[option]}
                  options={["json", "query"]}
                  onChangeCallBack={(value: keyof typeof DATA_QUERY_TYPE_OPTIONS["options"]) => {
                    handleOpen();
                    setChangedQueryTypeBeforeConfirmation(value);
                  }}
                />
              );
            }}
          </ActionDialog>
          {editorSettings}
          <Stack direction="row" spacing={1}>
            <Tooltip
              title={<Typography variant="body1">{`${layoutIsActive ? "Disable" : "Enable"} Layout`}</Typography>}
            >
              <Switch size="small" checked={layoutIsActive} onClick={() => toggleLayoutActive(!layoutIsActive)} />
            </Tooltip>

            <Typography>{layoutIsActive ? "Disable" : "Enable"} Layout</Typography>
          </Stack>
        </Stack>
      </Stack>
    </>
  );
};

type JSONConfigurationEditorProps = {
  columns: TDataTableColumnDefinition[];
  onChangeColumns: (columns: TDataTableColumnDefinition[]) => void;
  showJSONEditor?: boolean;
};

const JSONConfigurationEditor = ({ columns, onChangeColumns, showJSONEditor }: JSONConfigurationEditorProps) => {
  return showJSONEditor ? (
    <JSONEditor
      editorId="list-layout-json-editor"
      value={{ columns }}
      onChange={(jsonObject) => onChangeColumns(jsonObject?.columns || [])}
    />
  ) : (
    <ManageDataTableColumns columns={columns} onChangeColumns={onChangeColumns} />
  );
};

type ManageDataTableColumnsProps = {
  columns: TDataTableColumnDefinition[];
  onChangeColumns: (columns: TDataTableColumnDefinition[]) => void;
};

const ManageDataTableColumnsPopUpButton = ({ columns, onChangeColumns }: ManageDataTableColumnsProps) => {
  const LANG = useTranslatedText();

  return (
    <StyledOSDialog
      maxWidth="lg"
      popUpTitle={LANG.manageDataTableColumns}
      popUpIcon={<TableChartIcon color="primary" />}
      renderOpenButton={({ handleOpen }) => (
        <OSActionButton
          actionStyle="custom"
          customIcon={<TableChartIcon color="primary" />}
          variant="text"
          sx={{ justifyContent: "left" }}
          onClick={handleOpen}
        >
          {LANG.manageDataTableColumns}
        </OSActionButton>
      )}
    >
      <ManageDataTableColumns columns={columns} onChangeColumns={onChangeColumns} />
    </StyledOSDialog>
  );
};

const ManageDataTableColumns = ({ columns, onChangeColumns }: ManageDataTableColumnsProps) => {
  const LANG = useTranslatedText();

  const updateColumn = (index: number, key: keyof TDataTableColumnDefinition, value: string | boolean | number) => {
    const newColumns = [...columns];
    newColumns[index] = { ...newColumns[index], [key]: value };
    onChangeColumns(newColumns);
  };

  const addNewColumn = (index: number) => {
    const newColumn: TDataTableColumnDefinition = {
      col: `${index}`,
      label: "",
      link: false,
      staff_view: false,
    };

    onChangeColumns([...columns, newColumn]);
  };

  const removeColumn = (columnReference: string, index: number) => {
    onChangeColumns(columns.filter((col, idx) => !(col.col === columnReference && idx === index)));
  };

  const colSpan = 7;

  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>{LANG.sNo}</TableCell>
          <TableCell>{LANG.columnReference}</TableCell>
          <TableCell>{LANG.label}</TableCell>
          <TableCell>{LANG.linkToDetailsPage}</TableCell>
          <TableCell>{LANG.visibleToStaffOnly}</TableCell>
          <TableCell>{LANG.ProgressWidget}</TableCell>
          <TableCell>{LANG.order}</TableCell>
          <TableCell>{LANG.remove}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {columns.map((column, index) => (
          <TableRow key={`${column.col}-${index}`}>
            <TableCell>{index + 1}</TableCell>
            <TableCell>
              <TextField
                label={LANG.columnReference}
                defaultValue={column.col}
                onBlur={(e) => updateColumn(index, "col", e.target.value)}
              />
            </TableCell>
            <TableCell>
              <TextField
                label={LANG.label}
                defaultValue={column.label}
                onBlur={(e) => updateColumn(index, "label", e.target.value)}
              />
            </TableCell>
            <TableCell>
              <Tooltip title={<Typography variant="body1">{LANG.linkToDetailsPage}</Typography>}>
                <Switch
                  size="small"
                  checked={column.link}
                  onChange={(e) => updateColumn(index, "link", e.target.checked)}
                />
              </Tooltip>
            </TableCell>
            <TableCell>
              <Tooltip title={<Typography variant="body1">{LANG.visibleToStaffOnly}?</Typography>}>
                <Switch
                  size="small"
                  checked={column.staff_view}
                  onChange={(e) => updateColumn(index, "staff_view", e.target.checked)}
                />
              </Tooltip>
            </TableCell>
            <TableCell>
              <Tooltip
                title={
                  <Typography variant="body1">
                    {LANG.ProgressWidget}? {LANG.valueForDisplayDataMustBeNumber}
                  </Typography>
                }
                placement="top-start"
              >
                <OSDropDown
                  label={LANG.ProgressWidget}
                  options={{ "": "", linear: "Linear" }}
                  value={column.progress_widget}
                  onChangeCallBack={(value) => updateColumn(index, "progress_widget", value as string)}
                  fullWidth
                />
              </Tooltip>
            </TableCell>
            <TableCell>
              <Tooltip title={<Typography variant="body1">{LANG.order}?</Typography>}>
                <OSDropDown
                  label={LANG.order}
                  options={{ asc: "Ascending", desc: "Descending", "": "None" }}
                  value={column.order}
                  onChangeCallBack={(value) => updateColumn(index, "order", value as string)}
                  fullWidth
                />
              </Tooltip>
            </TableCell>
            <TableCell>
              <Tooltip title={<Typography>{LANG.remove}</Typography>}>
                <OSActionButton actionStyle="delete" variant="text" onClick={() => removeColumn(column.col, index)}>
                  {LANG.remove}
                </OSActionButton>
              </Tooltip>
            </TableCell>
          </TableRow>
        ))}
        <TableRow>
          <TableCell colSpan={colSpan - 1}></TableCell>
          <TableCell colSpan={1}>
            <OSIconButton
              color="primary"
              toolTipText={<Typography>{LANG.addNewColumn}</Typography>}
              icon={<AddBoxIcon />}
              onClick={() => addNewColumn(columns.length)}
            />
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
};

interface ProcessInstanceDataTableResultsPreviewProps {
  dataTableQueryResults: ProcessInstanceDataTableResult;
  columns: TDataTableColumnDefinition[];
  isLoading?: boolean;
  hasError?: boolean;
  errors?: any;
}

const ProcessInstanceDataTableResultsPreview = ({
  dataTableQueryResults,
  columns,
  isLoading,
  hasError,
  errors,
}: ProcessInstanceDataTableResultsPreviewProps) => {
  if (hasError) {
    return (
      <OSAlert severity="error" alertTitle="An error occured while trying to run your query.">
        {errors.map?.((error) => (
          <p>{error}</p>
        ))}
      </OSAlert>
    );
  }

  if (dataTableQueryResults?.data === undefined && !isLoading) {
    return <OSAlert>Your data preview will appear here when you run the query.</OSAlert>;
  }

  const data = dataTableQueryResults?.data || [];
  if (data.length === 0 && !isLoading) {
    return <OSAlert>No results for query.</OSAlert>;
  }

  return (
    <ProgressWrapper showLoader={isLoading} loaderType="linear">
      <SimpleTable
        headData={columns.map((column) => ({
          key: column.col,
          cell: column.label,
        }))}
        rows={data.map((row) => row.map((cell, cellIndex) => ({ key: `${cellIndex}`, cell })))}
      />
    </ProgressWrapper>
  );
};
